/* eslint-disable unicorn/consistent-function-scoping */
import type React from 'react';
import { Fragment, useEffect, useState } from 'react';
import { NavDropdown, Offcanvas } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import RecentAlertPopoverSkeleton from '../../pages/Home/components/RecentAlerts/components/Skelton';
import { ROUTE_PATHS } from '../../routes/routePaths';
import {
  useGetEncryptQuery,
  useGetHamburgerMenuOptions,
  useGetProductCategory,
  useGetRedirectKey,
} from '../../services/productServices';
import {
  useGetProductCategoryDetails,
  useRedirectReport,
} from '../../services/reportServices';
import { useUserAccess } from '../../services/userAccess';
import type {
  HamburgerMenuResponse,
  IProductModel,
} from '../../types/products';
import type { RenderStyle } from '../../types/redirect';
import { getRedirectPath, safeNavigate } from '../../util';
import { API_PRODUCT_RENDER_TYPE_ID } from '../../util/constants';
import { logger } from '../../util/logger';
import { captureEventInternal } from '../../util/posthog';
import './sidebar.css';

interface SidebarProps {
  show: boolean;
  handleClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ show, handleClose }) => {
  const { data: products, isLoading } = useGetProductCategory({ enabled: show });
  const { data: userAccess } = useUserAccess();
  const [activeDropdownCategoryId, setActiveDropdownCategoryId] = useState<
    number | null
  >(null);
  const [menuOptionsByCategoryId, setMenuOptionsByCategoryId] = useState<
    Record<number, HamburgerMenuResponse>
  >({});
  const [productDetailsByCategoryId, setProductDetailsByCategoryId] = useState<
    Record<number, Array<RenderStyle>>
  >({});
  const navigate = useNavigate();
  const [encryptInput, setEncryptInput] = useState<string | null>(null);
  const { data: encryptedQuery } = useGetEncryptQuery(encryptInput ?? '');
  const getRedirectKeyMutation = useGetRedirectKey();
  const redirectReportMutation = useRedirectReport();
  const { data: fetchedMenuOptions, isLoading: isLoadingMenuOptions } =
    useGetHamburgerMenuOptions(activeDropdownCategoryId ?? 0, { enabled: show });
  const { data: fetchedCategoryDetails } = useGetProductCategoryDetails(
    activeDropdownCategoryId ? String(activeDropdownCategoryId) : ''
  );

  useEffect(() => {
    if (!show) {
      setActiveDropdownCategoryId(null);
    }
  }, [show]);

  useEffect(() => {
    if (fetchedMenuOptions && activeDropdownCategoryId !== null) {
      setMenuOptionsByCategoryId((previous) => ({
        ...previous,
        [activeDropdownCategoryId]: fetchedMenuOptions,
      }));
    }
  }, [fetchedMenuOptions, activeDropdownCategoryId]);

  useEffect(() => {
    if (fetchedCategoryDetails?.data && activeDropdownCategoryId !== null) {
      setProductDetailsByCategoryId((previous) => ({
        ...previous,
        [activeDropdownCategoryId]: fetchedCategoryDetails.data,
      }));
    }
  }, [fetchedCategoryDetails, activeDropdownCategoryId]);

  useEffect(() => {
    if (encryptInput && encryptedQuery?.data) {
      const encryptedValue = encryptedQuery.data;
      const path = `${ROUTE_PATHS.PRODUCT}?eURL=${encodeURIComponent(
        encryptedValue
      )}`;

      safeNavigate(navigate, path);
      handleClose();
      setEncryptInput(null);
    }
  }, [encryptInput, encryptedQuery, navigate, handleClose]);

  // Reusable function to track hamburger menu clicks
  const trackHamburgerMenuClick = (
    text: string,
    href: string,
    additionalProperties?: {
      categoryName?: string;
      categoryId?: number;
      categoryType?: number;
      productId?: number;
    }
  ): void => {
    void captureEventInternal('hamburger_menu_click', {
      text,
      href,
      ...additionalProperties,
    });
  };

  // Reusable function to track action menu link clicks
  const trackActionMenuLinkClick = (text: string, href: string): void => {
    void captureEventInternal('action_menu_link_click', { text, href });
  };

  // Generate link for product - supports right-click "open in new tab"
  const getProductLink = (product: IProductModel): string => {
    // Direct external links
    if (product?.htmlType === -1 && product?.htmlText) {
      return product.htmlText;
    }
    // Use redirect route for others - includes categoryId and categoryType for redirect logic
    const queryParameters = new URLSearchParams();
    if (product?.categoryId) {
      queryParameters.set('categoryId', product.categoryId.toString());
    }
    if (product?.categoryType) {
      queryParameters.set('categoryType', product.categoryType.toString());
    }
    const queryString = queryParameters.toString();
    return `${ROUTE_PATHS.PRODUCT_REDIRECT.replace(':productId', product?.renderStyleId?.toString() || '')}${queryString ? `?${queryString}` : ''}`;
  };

  const handleProductClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    product: IProductModel
  ) => {
    event.preventDefault();
    if (product?.htmlType === -1 && product?.htmlText) {
      window.open(product.htmlText, '_blank');
      handleClose();
      return;
    }
    getRedirectKeyMutation.mutate(product?.renderStyleId?.toString() || '', {
      onSuccess: async (data) => {
        const redirectData = data?.data;
        if (redirectData?.htmlType === 2 && redirectData?.htmlText) {
          window.open(redirectData.htmlText, '_blank');
          handleClose();
        } else switch (redirectData?.redirect) {
          case 'PRODUCT_HOME_PAGE': {
            const query = `productId=${product?.renderStyleId}&categoryId=${product?.categoryId}&categoryType=${product?.categoryType}`;
            const uniqueQuery = `${query}&_=${Date.now()}`;
            setEncryptInput(uniqueQuery);

            break;
          }
          case 'FIND_REPORTS': {
            await navigate(
              `${ROUTE_PATHS.FIND_REPORTS}?categoryId=${product?.categoryId}&categoryType=${product?.categoryType}&source=${redirectData?.productId}`
            );
            handleClose();

            break;
          }
          case 'REPORT': {
            const redirectInfo = redirectData as {
              reportId?: number | string;
              productId?: number | string;
              pageType?: string;
            };

            const reportId = Number(redirectInfo?.reportId);
            const productId = Number(redirectInfo?.productId);
            const pageType = redirectInfo?.pageType ?? 'home';

            if (Number.isNaN(reportId) || Number.isNaN(productId)) {
              return;
            }

            redirectReportMutation.mutate(
              {
                reportId,
                renderStyle: productId,
                pageType,
                entityCheck: 1,
              },
              {
                onSuccess: (redirectResponse) => {
                  const reportRedirectData = redirectResponse?.data;
                  if (reportRedirectData) {
                    const path = getRedirectPath(reportRedirectData);
                    void navigate(path);
                  }
                  handleClose();
                },
                onError: (error) => {
                  logger.error('Report redirect error:', error);
                  handleClose();
                },
              }
            );

            break;
          }
          default: {
            // eslint-disable-next-line no-console
            logger.warn('No valid action found for the returned data.');
            handleClose();
          }
        }

        trackHamburgerMenuClick(
          product.renderStyleName,
          redirectData?.htmlType === 2
            ? redirectData?.htmlText
            : redirectData?.redirect ?? '',
          {
            categoryName: product.categoryName,
            categoryId: product.categoryId,
            categoryType: product.categoryType,
            productId: product.renderStyleId,
          }
        );
      },
      onError: (error) => {
        logger.error('API call error:', error);
        handleClose();
      },
    });
  };

  const renderSkeleton = () => {
    const SKELETON_IDS = ['item-a', 'item-b', 'item-c'];
    return SKELETON_IDS.map((item) => (
      <div key={`skeleton-category-${item}`} className="mb-4">
        <div className="d-flex align-items-center mb-2">
          <div className="placeholder-glow w-75">
            <span className="placeholder col-6" />
          </div>
          <div className="placeholder-glow ms-auto me-3">
            <span className="placeholder col-2" />
          </div>
        </div>
        <ul className="ao_sildeItem list-unstyled">
          {SKELETON_IDS.map((_, itemIndex) => (
            <li
              key={`skeleton-item-${item}-${itemIndex}`}
              className="placeholder-glow"
            >
              <span className="placeholder col-8" />
            </li>
          ))}
        </ul>
      </div>
    ));
  };

  const handleAddNewReportClick = (
    categoryId: number,
    categoryType: number,
    menuName: string,
    itemPath: string
  ) => {
    const details = productDetailsByCategoryId[categoryId];
    const base = `catType=${categoryType}&catID=${categoryId}`;
    // Once details are available, build the exact path from the 3 product buckets
    // (normal / part-1-only / API type renderTypeId=105). While details are still
    // loading, fall back to the select-product page, which re-derives the same buckets
    let customPath = `${ROUTE_PATHS.ADD_NEW_REPORT}?${base}`;
    if (details) {
      const normal = details.filter(
        (p) => (p.onlyAllowEditPart1 ?? 0) === 0 && (p.renderTypeId ?? 0) !== API_PRODUCT_RENDER_TYPE_ID
      );
      const onlyPart1 = details.filter(
        (p) => (p.onlyAllowEditPart1 ?? 0) === 1 && (p.renderTypeId ?? 0) !== API_PRODUCT_RENDER_TYPE_ID
      );
      const apiType = details.filter(
        (p) => (p.renderTypeId ?? 0) === API_PRODUCT_RENDER_TYPE_ID
      );
      const nonEmpty = [normal, onlyPart1, apiType].filter((bucket) => bucket.length > 0);

      if (nonEmpty.length > 1) {
        customPath = `${ROUTE_PATHS.ADD_NEW_REPORT}?${base}`;
      } else if (apiType.length > 0) {
        const renderTypeId = apiType[0]?.renderTypeId ?? API_PRODUCT_RENDER_TYPE_ID;
        customPath = `${ROUTE_PATHS.OPINION_ADD}?RenderStyleNode=${apiType[0]?.renderStyleId}&${base}&showPart1Only=true&renderTypeId=${renderTypeId}`;
      } else if (onlyPart1.length > 0) {
        customPath = `${ROUTE_PATHS.OPINION_ADD}?RenderStyleNode=${onlyPart1[0]?.renderStyleId}&${base}&showPart1Only=true`;
      } else {
        customPath = `${ROUTE_PATHS.OPINION_ADD}?${base}`;
      }
    }

    return (
      <NavDropdown.Item
        key={menuName}
        as={Link}
        to={customPath}
        onClick={(event) => {
          const isRegularClick = 
            event.button === 0 && 
            !event.ctrlKey && 
            !event.metaKey && 
            !event.shiftKey;
          
          if (isRegularClick) {
            event.preventDefault();
            trackActionMenuLinkClick(menuName, itemPath);
            handleClose();
            safeNavigate(navigate, customPath);
          } else {
            trackActionMenuLinkClick(menuName, itemPath);
            handleClose();
          }
        }}
      >
        {menuName}
      </NavDropdown.Item>
    );
  };

  const handleAddNewPageClick = (
    categoryId: number,
    categoryType: number,
    menuName: string,
    itemPath: string
  ) => {
    const customPath = `${ROUTE_PATHS.EDIT_PAGE}/pageAdd?&categoryId=${categoryId}&categoryType=${categoryType}`;
    return (
      <NavDropdown.Item
        key={menuName}
        as={Link}
        to={customPath}
        onClick={(event) => {
          const isRegularClick = 
            event.button === 0 && 
            !event.ctrlKey && 
            !event.metaKey && 
            !event.shiftKey;
          
          if (isRegularClick) {
            event.preventDefault();
            trackActionMenuLinkClick(menuName, itemPath);
            handleClose();
            safeNavigate(navigate, customPath);
          } else {
            trackActionMenuLinkClick(menuName, itemPath);
            handleClose();
          }
        }}
      >
        {menuName}
      </NavDropdown.Item>
    );
  };

  const handleAddNewGridClick = (
    categoryId: number,
    categoryType: number,
    menuName: string,
    itemPath: string
  ) => {
    const customPath = `${ROUTE_PATHS.EDIT_GRID}?categoryId=${categoryId}&categoryType=${categoryType}&gridId=0`;
    return (
      <NavDropdown.Item
        key={menuName}
        as={Link}
        to={customPath}
        onClick={(event) => {
          const isRegularClick = 
            event.button === 0 && 
            !event.ctrlKey && 
            !event.metaKey && 
            !event.shiftKey;
          
          if (isRegularClick) {
            event.preventDefault();
            trackActionMenuLinkClick(menuName, itemPath);
            handleClose();
            safeNavigate(navigate, customPath);
          } else {
            trackActionMenuLinkClick(menuName, itemPath);
            handleClose();
          }
        }}
      >
        {menuName}
      </NavDropdown.Item>
    );
  };


  const handleDefaultMenuClick = (
    itemPath: string,
    categoryId: number,
    categoryType: number,
    menuName: string
  ) => (
    <NavDropdown.Item
      key={menuName}
      as={Link}
      to={`${itemPath}?categoryId=${categoryId}&categoryType=${categoryType}`}
      onClick={() => {
        trackActionMenuLinkClick(menuName, itemPath);
        handleClose();
      }}
    >
      {menuName}
    </NavDropdown.Item>
  );

  const renderMenuItem = (
    item: HamburgerMenuResponse['data']['settingsOptions'][number]['menuItems'][number],
    categoryId: number,
    categoryType: number
  ) => {
    if (
      item.menuName === 'Add New Report' ||
      item.path === '/add-new-report' ||
      item.path === ROUTE_PATHS.ADD_NEW_REPORT_ONLY_PART1
    ) {
      return handleAddNewReportClick(categoryId, categoryType, item.menuName, item.path);
    }

    if (item.path === '/add-new-page') {
      return handleAddNewPageClick(categoryId, categoryType, item.menuName, item.path);
    }
 if (item.path === '/add-new-grid') {
      return handleAddNewGridClick(categoryId, categoryType, item.menuName, item.path);
    }

    return handleDefaultMenuClick(item.path, categoryId, categoryType, item.menuName);
  };

  const renderOptionSection = (
    option: HamburgerMenuResponse['data']['settingsOptions'][number],
    index: number,
    optionsLength: number,
    categoryId: number,
    categoryType: number
  ) => (
    <Fragment key={option.section}>
      <NavDropdown.Item className="dropdown-header">
        <h6 className="mb-0">{option.section}</h6>
      </NavDropdown.Item>
      {option.menuItems.map((item) => renderMenuItem(item, categoryId, categoryType))}
      {index !== optionsLength - 1 && <NavDropdown.Divider />}
    </Fragment>
  );

  const renderDropdown = (
    iconSource: string,
    options: HamburgerMenuResponse['data']['settingsOptions'],
    categoryId: number,
    categoryType: number,
    className?: string
  ) => (
    <NavDropdown
      title={<img src={iconSource} alt="dropdown icon" />}
      align="end"
      onToggle={(isOpen) => isOpen && setActiveDropdownCategoryId(categoryId)}
      className={className}
    >
      {isLoadingMenuOptions
        ? <RecentAlertPopoverSkeleton length={7} />
        : options.map((option, index) =>
            renderOptionSection(option, index, options.length, categoryId, categoryType)
      )}
    </NavDropdown>
  );

  return (
    <>
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="start"
      className="ao_sidebar"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          <img
            src="/assets/aoslogin_brand_update.png"
            alt="Logo"
            className="img-fluid"
            style={{ maxWidth: '150px' }}
          />
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <div
          className="offcanvas offcanvas-start show"
          tabIndex={-1}
          id="offcanvasNavbar"
          style={{ visibility: 'visible' }}
        >
          <div className="offcanvas-header">
            <h5 className="offcanvas-title" id="offcanvasNavbarLabel">
              <Link to={ROUTE_PATHS?.CATEGORY_HOME} onClick={handleClose}>
                <img
                  src="/assets/images/newdesign/aosphere_an_affiliate_logo.svg?dt=08_02_2024"
                  height="35"
                  alt="AOSphere"
                  title="AOSphere"
                />
              </Link>
            </h5>
            <button
              type="button"
              className="btn-close text-reset"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
              onClick={handleClose}
            />
          </div>

          <div className="offcanvas-body">
            {!userAccess?.guestUser && (
              <>
                <h2 className="sectionTitle mb-0 d-flex">
                  <Link
                    to={ROUTE_PATHS.ALL_ALERTS}
                    onClick={() => {
                      trackHamburgerMenuClick('See All Alerts', 'all-alerts');
                      handleClose();
                      safeNavigate(navigate, (ROUTE_PATHS.ALL_ALERTS));
                    }}
                  >
                    See All Alerts
                  </Link>
                  <span className="ms-auto badge ao_badgeProgress rounded-pill minWidth0" />
                </h2>
                <hr />
              </>
            )}

            <h2 className="sectionTitle">Your Subscriptions</h2>
            <hr />

            {isLoading
              ? renderSkeleton()
              : products?.data.map((category, index) => {
                const categoryId = category.categoryId;
                const categoryType = category.categoryType;
                return (
                  <div key={categoryId}>
                    <div className="d-flex">
                      <h6 className="ao_sildeMenuTitle flex-grow-1">
                        {category.categoryName}
                      </h6>
                      {
                        userAccess?.editAccess && (
                        <div className="aos_sidebar_setting">
                          {renderDropdown(
                            '/assets/images/newdesign/settings.svg',
                            menuOptionsByCategoryId[categoryId]?.data
                              ?.settingsOptions ?? [],
                            categoryId,
                            categoryType,
                            'me-3'
                          )}
                        </div>
                        )
                      }
                      <div className="aos_sidebar_setting">
                        {renderDropdown(
                          '/assets/images/newdesign/dots.svg',
                          menuOptionsByCategoryId[categoryId]?.data
                            ?.ellipsesOptions ?? [],
                          categoryId,
                          categoryType
                        )}
                      </div>
                    </div>

                    <ul className="ao_sildeItem">
                      {category.productModelList.map((product) => {
                        const productLink = getProductLink(product);
                        const isExternalLink = product?.htmlType === -1 && product?.htmlText;
                        
                        return (
                          <li key={product.renderStyleId}>
                            {isExternalLink ? (
                              <a
                                href={productLink}
                                title={product.renderStyleName}
                                onClick={(event) =>
                                  handleProductClick(event, product)
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {product.renderStyleName}
                              </a>
                            ) : (
                              <Link
                                to={productLink}
                                title={product.renderStyleName}
                                onClick={(event) =>
                                  handleProductClick(event, product)
                                }
                              >
                                {product.renderStyleName}
                              </Link>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    {index !== products.data.length - 1 && <hr />}
                  </div>
                );
              })}

            {userAccess?.guestUser && (
              <>
                {products?.data && products.data.length > 0 && (
                  <hr className="my-3" />
                )}

                <div className="d-flex">
                  <h6 className="ao_sildeMenuTitle flex-grow-1">Reviews</h6>
                  <div className="dropdown me-3 dropup" />
                </div>

                <ul className="ao_sildeItem">
                  <li>
                    <Link
                      to={ROUTE_PATHS.GUEST_USER_REPORTS}
                      onClick={handleClose}
                    >
                      Reports
                    </Link>
                  </li>
                </ul>
              </>
            )}
          </div>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
    </>
  );
};

export default Sidebar;
